import styled from 'styled-components';

// The sedes of a partner as a list: group header, search and rows. Shared by
// the profile menu and the chat, so both look the same. The values are the
// ones of the panel design (docs/design/selector-espacios of iventas-multisede).

// The page sets a font on every element: a text that only inherits its font
// gets that one, so each text of the panel sets its own.
export const panelFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

export const panelColors = {
  text: '#1A2238',
  textSoft: '#5B6475',
  textMuted: '#4A5468',
  icon: '#6B7385',
  border: '#E9ECF1',
  surface: '#F3F4F6',
  focus: '#2563EB',
  lateText: '#9A3412',
  lateBg: '#FFEDD5',
};

export const focusRing = `
  &:focus-visible {
    outline: 2px solid ${panelColors.focus};
    outline-offset: 2px;
  }
`;

export const waitingPillLate = `
  background: ${panelColors.lateBg};
  color: ${panelColors.lateText};
`;

export const SedeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 2px;
`;

export const SedeHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const GroupName = styled.h4`
  font-family: ${panelFont};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.2;
  color: ${panelColors.text};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PartnerPill = styled.span`
  flex: none;
  padding: 5px 10px;
  border-radius: 999px;
  background: #EAF1FE;
  font-family: ${panelFont};
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
  color: #1D4ED8;
  white-space: nowrap;
`;

export const SedeSubtitle = styled.p`
  font-family: ${panelFont};
  font-weight: 400;
  font-size: 12px;
  line-height: 1.2;
  color: ${panelColors.textSoft};
  margin: 0;
`;

export const SedeSearch = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  flex-shrink: 0;
  box-sizing: border-box;
  margin: 0;
  padding: 0 8px 0 12px;
  background: ${panelColors.surface};
  border-radius: 12px;
  color: ${panelColors.textSoft};

  &:focus-within {
    outline: 2px solid ${panelColors.focus};
    outline-offset: 2px;
  }

  svg {
    flex: none;
  }

  input {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    font-family: ${panelFont};
    font-weight: 400;
    font-size: 14px;
    color: ${panelColors.text};

    /* Under 16px a phone zooms the page in when the field takes the focus. */
    @media (pointer: coarse) {
      font-size: 16px;
    }
  }

  input::placeholder {
    color: ${panelColors.icon};
    opacity: 1;
  }
`;

// The shortcut that opens the panel on its search. A phone has no keyboard to
// press it on.
export const SearchShortcut = styled.kbd`
  flex: none;
  padding: 3px 6px;
  border: 1px solid #DCE1EA;
  border-radius: 6px;
  background: #FFFFFF;
  box-shadow: none;
  font-family: ${panelFont};
  font-weight: 600;
  font-size: 11px;
  line-height: 1.2;
  color: ${panelColors.textMuted};
  white-space: nowrap;

  @media (pointer: coarse) {
    display: none;
  }
`;

export const SedeSectionLabel = styled.p`
  padding: 2px 2px 0;
  font-family: ${panelFont};
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
  color: ${panelColors.textMuted};
  margin: 0;
`;

// The rows scroll on their own: with many sedes the parent keeps its height.
// Each parent sets how tall the list may get (the profile menu, the chat).
export const SedeRows = styled.ul`
  list-style: none;
  /* Room for the focus ring of the rows, without moving them. */
  padding: 2px;
  margin: -2px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: min(280px, 36vh);
  overflow-y: auto;
`;

export const SedeItem = styled.li`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 1px solid ${(props) => (props.active ? '#395E4F' : panelColors.border)};
  border-radius: 14px;
  background: ${(props) => (props.active ? '#F3F9F7' : '#FFFFFF')};
`;

export const SedeRowButton = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 62px;
  padding: 8px 4px 8px 10px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: ${panelColors.text};
  text-align: left;
  cursor: ${(props) => (props.active ? 'default' : 'pointer')};
  ${focusRing}

  /* Without the "new tab" action the row ends here. */
  &:last-child {
    padding-right: 10px;
  }

  ${(props) => !props.active && `
    &:hover {
      background: rgba(26, 34, 56, 0.04);
    }
  `}
`;

export const SedeMonogram = styled.span`
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* The sede color is set inline over this one. The palette of the backend
     is dark, saturated colors: white text on it. */
  background-color: #2C5F4E;
  color: #FFFFFF;
  font-family: ${panelFont};
  font-weight: 700;
  font-size: 14px;
`;

export const SedeContent = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SedeTitleLine = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// The only text of a row that may be cut short.
export const SedeLabel = styled.span`
  min-width: 0;
  font-family: ${panelFont};
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  color: ${panelColors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// The active sede is told apart by the border and the background of its row;
// this is the same news for a screen reader.
export const ActiveMark = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const SedePhoneLine = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// Icon, first phone and "+N": never cut and never wrapped.
export const SedePhones = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #1F8A55;
  white-space: nowrap;

  svg {
    flex: none;
  }
`;

const phoneText = `
  flex-shrink: 0;
  font-family: ${panelFont};
  font-weight: 600;
  font-size: 13px;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
`;

export const SedePhone = styled.span`
  ${phoneText}
  color: #2B3348;
`;

export const SedeMorePhones = styled.span`
  ${phoneText}
  color: ${panelColors.icon};
`;

// The room of the waiting pill, at the right of the row and one line tall.
// On a phone there is no room for "N esperando" next to the number of the
// sede: the slot narrows, the word wraps to a second line that is not shown
// and the pill is left with its count; it never goes away.
export const WaitingSlot = styled.span`
  flex: none;
  height: 24px;
  overflow: hidden;
  font-size: 12px;
  line-height: 24px;
  text-align: right;

  @media (max-width: 420px) {
    /* A count of three digits. */
    max-width: 44px;
  }
`;

// "late" stands out: someone has waited longer than they should. Inline, so
// that the part left on the first line is still a whole pill.
export const WaitingPill = styled.span`
  padding: 4px 9px;
  border-radius: 999px;
  font-family: ${panelFont};
  font-weight: 600;
  font-size: 12px;
  line-height: 24px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  background: #EEF1F5;
  color: #3A4458;

  &[data-emphasis='late'] {
    ${waitingPillLate}
  }
`;

export const NewTabButton = styled.button`
  flex: none;
  width: 44px;
  height: 44px;
  margin-right: 4px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${panelColors.icon};
  cursor: pointer;
  ${focusRing}

  &:hover {
    background: ${panelColors.surface};
  }
`;

export const SedeEmptyLine = styled.p`
  padding: 16px 8px;
  font-family: ${panelFont};
  font-weight: 400;
  font-size: 13px;
  line-height: 1.4;
  color: ${panelColors.textMuted};
  margin: 0;
`;

export const SedeMutedLine = styled.p`
  padding: 0 2px;
  font-family: ${panelFont};
  font-weight: 400;
  font-size: 12px;
  line-height: 1.4;
  color: ${panelColors.textSoft};
  margin: 0;
`;
